/**
 * RunStack — the paper stack behind a NODE OF THE GRAPH that has runs on it.
 *
 * Founder, 2026-08-26: the fibre effect, and everything that comes with it,
 * "on the normal graph as well … when the graph itself runs and travels
 * between nodes". A member satellite already draws its runs as a pile of paper
 * behind its tile; a STEP with runs on it now draws the same pile behind its
 * own tile, because it is the same statement about the same kind of thing.
 *
 * Deliberately the same maths: `runningStack`/`MAX_SHEETS` are IMPORTED from
 * MemberNode rather than restated, so "how many sheets is N runs" has ONE
 * answer on this canvas and the two piles can never disagree about the cap.
 * What differs is only scale, and that is CSS — the sheet takes `inset: 0` and
 * `border-radius: inherit` from whatever tile it is placed in (an 88px icon
 * box, a rounded Start pill), so there is no per-node-type geometry here.
 *
 * Usage, three lines and no branching, in every node type:
 *   • put `gwe-run-stack-host` on the TILE (the bordered box) and render
 *     `<RunStack activeRuns={…} />` inside it;
 *   • put `gwe-node-running` on the node ROOT when `activeRuns > 0` — that is
 *     what lights the label and the tile's edge.
 * Nothing renders at 0 runs, so an idle node is exactly the node that was
 * always drawn.
 */
import React from 'react';
import { runningStack, MAX_SHEETS } from './MemberNode';
import './RunStack.css';

export { runningStack, MAX_SHEETS };

/** The class the node ROOT carries while runs are on it ('' when idle). */
export function runningClass(activeRuns) {
  return runningStack(activeRuns).running ? 'gwe-node-running' : '';
}

export const RunStack = ({ activeRuns = 0 }) => {
  const { running, sheets, overflow } = runningStack(activeRuns);
  if (!running) return null;
  return (
    <>
      {Array.from({ length: sheets }, (_, i) => (
        <div key={i} className="gwe-run-stack__sheet" style={{ '--sheet': i + 1 }} aria-hidden />
      ))}
      {overflow > 0 && (
        <div className="gwe-run-stack__overflow" aria-label={`${overflow} more runs`}>
          +{overflow}
        </div>
      )}
    </>
  );
};

export default RunStack;
